import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Grid,
  Divider
} from '@mui/material';
import {
  AccountCircle as AccountIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getUsageStats, getUserTier, formatCredits, getUsagePercentage, getUsageColor, getWarningLevel } from '../../services/usageService';

// Wyspur brand colors
const wyspurColors = {
  blue: '#0f71fb',    // Success/positive
  orange: '#ffaa01',  // Warning/70% threshold  
  pink: '#df3e99'     // Error/critical
};

/**
 * UsageStatsWidget - Displays user's credit usage and tier information
 * 
 * This component shows:
 * - Credits remaining
 * - Usage percentage
 * - User tier
 * - Warning indicators for low credits
 */
export default function UsageStatsWidget({ compact = false, showDetails = true }) {
  const [usageStats, setUsageStats] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Fetch usage data
  useEffect(() => {
    fetchUsageData();
  }, []);

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsData, tierData] = await Promise.all([
        getUsageStats(),
        getUserTier()
      ]);
      
      if (statsData.success) {
        console.log('[UsageStatsWidget] Usage stats received:', statsData.data);
        setUsageStats(statsData.data);
      } else {
        console.error('[UsageStatsWidget] Usage stats failed:', statsData);
      }
      
      if (tierData.success) {
        console.log('[UsageStatsWidget] Tier data received:', tierData.data);
        setTierInfo(tierData.data);
      } else {
        console.error('[UsageStatsWidget] Tier data failed:', tierData);
      }
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage information');
    } finally {
      setLoading(false);
    }
  };

  // Calculate usage metrics with safe defaults - use backend values
  const creditsUsed = usageStats?.creditsUsed || 0;
  const usagePercentage = usageStats?.percentUsed || 
    (usageStats && tierInfo ? getUsagePercentage(creditsUsed, tierInfo.credits_per_month || usageStats.creditsTotal || 0) : 0);
  
  const warningLevel = getWarningLevel(usagePercentage || 0);
  const usageColor = getUsageColor(usagePercentage || 0);
  
  // Get Wyspur color based on warning level
  const getWyspurColor = (level) => {
    switch (level) {
      case 'critical': return wyspurColors.pink;
      case 'low': return wyspurColors.orange;
      case 'none': return wyspurColors.blue;
      default: return wyspurColors.blue;
    }
  };

  // Handle loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LinearProgress size={20} />
        {!compact && <Typography variant="caption">Loading...</Typography>}
      </Box>
    );
  }

  // Handle error state
  if (error || !usageStats || !tierInfo) {
    return (
      <Tooltip title={error || 'Usage information unavailable'}>
        <Chip
          icon={<WarningIcon />}
          label={compact ? 'N/A' : 'Usage N/A'}
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    );
  }

  // Use backend's authoritative credit values instead of calculating
  const creditsRemaining = usageStats?.creditsAvailable || 0;
  const creditsTotal = usageStats?.creditsTotal || tierInfo?.credits_per_month || 0;
  const isUnlimited = creditsTotal === -1;
  
  // Debug logging for credit calculation
  console.log('[UsageStatsWidget] Credit calculation:', {
    tierInfo: tierInfo,
    usageStats: usageStats,
    backendCreditsPerMonth: tierInfo?.credits_per_month,
    backendCreditsTotal: usageStats?.creditsTotal,
    creditsTotal: creditsTotal,
    creditsRemaining: creditsRemaining,
    isUnlimited: isUnlimited
  });

  // Compact view (for header)
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={`${formatCredits(creditsRemaining)} credits remaining (${tierInfo?.displayName || tierInfo?.name || 'Unknown'})`}>
          <Chip
            icon={warningLevel === 'critical' ? <WarningIcon /> : <CheckCircleIcon />}
            label={isUnlimited ? 'Unlimited' : formatCredits(creditsRemaining)}
            variant="outlined"
            onClick={() => showDetails && setDetailsOpen(true)}
            sx={{ 
              cursor: showDetails ? 'pointer' : 'default',
              color: getWyspurColor(warningLevel),
              borderColor: getWyspurColor(warningLevel),
              '& .MuiChip-icon': { color: getWyspurColor(warningLevel) }
            }}
          />
        </Tooltip>
        {showDetails && (
          <UsageDetailsDialog
            open={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            usageStats={usageStats}
            tierInfo={tierInfo}
            onRefresh={fetchUsageData}
          />
        )}
      </Box>
    );
  }

  // Full view (for sidebar or dedicated area)
  return (
    <Box sx={{ width: '100%' }}>
      {/* Current Usage - Full Width */}
      <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.paper', mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Credits Used</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {formatCredits(creditsUsed)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Credits Remaining</Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isUnlimited ? 'Unlimited' : formatCredits(creditsRemaining)}
            </Typography>
          </Grid>
          {!isUnlimited && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Usage: {usagePercentage}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={usagePercentage}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getWyspurColor(warningLevel)
                  }
                }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Your Plan and Billing Cycle - Side by Side */}
      <Grid container spacing={2}>
        {/* Your Plan */}
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.paper', height: '100%' }}>
            <Box sx={{ mb: 1.5 }}>
              <Chip
                label={tierInfo?.displayName || tierInfo?.name || 'Unknown'}
                size="small"
                sx={{
                  color: wyspurColors.blue,
                  borderColor: wyspurColors.blue
                }}
                variant="outlined"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Monthly Credits
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.25 }}>
              {isUnlimited ? 'Unlimited' : formatCredits(creditsTotal)}
            </Typography>
          </Paper>
        </Grid>

        {/* Billing Cycle */}
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.paper', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption" color="text.secondary">Next Reset</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
              {getNextResetDate(usageStats.resetDate).toLocaleDateString(undefined, {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {showDetails && (
        <UsageDetailsDialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          usageStats={usageStats}
          tierInfo={tierInfo}
          onRefresh={fetchUsageData}
        />
      )}
    </Box>
  );
}

/**
 * Calculate the next billing cycle reset date
 * If the current reset date has passed, show next month's reset date
 */
function getNextResetDate(resetDate) {
  if (!resetDate) return new Date();
  
  const currentDate = new Date();
  const currentResetDate = new Date(resetDate);
  
  // If the reset date for this month has already passed, show next month's reset date
  if (currentResetDate <= currentDate) {
    const nextResetDate = new Date(currentResetDate);
    nextResetDate.setMonth(nextResetDate.getMonth() + 1);
    return nextResetDate;
  }
  
  return currentResetDate;
}

/**
 * Usage Details Dialog - Shows comprehensive usage information
 */
function UsageDetailsDialog({ open, onClose, usageStats, tierInfo, onRefresh }) {
  // Use backend's authoritative credit values instead of calculating
  const creditsRemaining = usageStats?.creditsAvailable || 0;
  const creditsUsed = usageStats?.creditsUsed || 0;
  const creditsTotal = usageStats?.creditsTotal || tierInfo?.credits_per_month || 0;
  const usagePercentage = usageStats?.percentUsed || getUsagePercentage(creditsUsed, creditsTotal);
  const isUnlimited = creditsTotal === -1;
  
  // Get Wyspur color based on warning level
  const getWyspurColor = (level) => {
    switch (level) {
      case 'critical': return wyspurColors.pink;
      case 'low': return wyspurColors.orange;
      case 'none': return wyspurColors.blue;
      default: return wyspurColors.blue;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountIcon />
          <Typography variant="h6">Usage & Billing Details</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Current Usage */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Current Usage</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Credits Used</Typography>
                  <Typography variant="h6">{formatCredits(usageStats.creditsUsed)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Credits Remaining</Typography>
                  <Typography variant="h6">
                    {isUnlimited ? 'Unlimited' : formatCredits(creditsRemaining)}
                  </Typography>
                </Grid>
                {!isUnlimited && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Usage: {usagePercentage}%
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={usagePercentage}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getWyspurColor(getWarningLevel(usagePercentage))
                        }
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>

          {/* Tier Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Your Plan</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Your Plan</Typography>
                  <Chip 
                    label={tierInfo?.displayName || tierInfo?.name || 'Unknown'} 
                    size="small" 
                    sx={{
                      color: wyspurColors.blue,
                      borderColor: wyspurColors.blue
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Monthly Credits</Typography>
                  <Typography variant="body1">
                    {isUnlimited ? 'Unlimited' : formatCredits(creditsTotal)}
                  </Typography>
                </Grid>
                {/* Temporarily hidden while limits are disabled for testing
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Concurrent Jobs</Typography>
                  <Typography variant="body1">
                    {tierInfo.maxConcurrentJobs === -1 ? 'Unlimited' : tierInfo.maxConcurrentJobs}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Websites per Job</Typography>
                  <Typography variant="body1">
                    {tierInfo.maxWebsitesPerJob === -1 ? 'Unlimited' : formatCredits(tierInfo.maxWebsitesPerJob)}
                  </Typography>
                </Grid>
                */}
              </Grid>
            </Paper>
          </Grid>

          {/* Reset Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>Billing Cycle</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">Next Reset</Typography>
              <Typography variant="body1">
                {getNextResetDate(usageStats.resetDate).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Your credits will reset to {isUnlimited ? 'unlimited' : formatCredits(creditsTotal)} on this date.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onRefresh} color="primary">
          Refresh
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}